import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Loader2, Sliders, Save } from "lucide-react";

/**
 * AdminSettingsCard
 * Read/write control panel for platform_settings — the single-source-of-truth
 * knob store for landing-page composition (Spotlight rail, Trending rail).
 * Backed by GET/PUT /api/admin/settings.
 */
export default function AdminSettingsCard() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/settings`);
      setSettings(r.data);
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = (key, value) => {
    setSettings((s) => ({ ...s, [key]: value }));
    setDirty(true);
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/admin/settings`,
        {
          spotlight_founding_count: Number(settings.spotlight_founding_count),
          spotlight_total_count: Number(settings.spotlight_total_count),
          spotlight_shuffle_fillers: !!settings.spotlight_shuffle_fillers,
          trending_show_philanthropy: !!settings.trending_show_philanthropy,
          trending_show_influencer: !!settings.trending_show_influencer,
        }
      );
      setSettings(r.data);
      setDirty(false);
      toast.success("Settings saved · site updates on next page load");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 flex items-center gap-2 text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading settings…
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 space-y-6" data-testid="admin-settings-card">
      <div>
        <h2 className="font-heading text-xl text-tbc-navy flex items-center gap-2">
          <Sliders className="w-5 h-5 text-tbc-gold" /> Landing Page Composition
        </h2>
        <p className="text-sm text-gray-500 font-body">
          Fine-tune what shows on the homepage — no redeploy needed. Changes
          take effect on next page load.
        </p>
      </div>

      {/* Spotlight rail */}
      <div className="space-y-4 border-t border-gray-100 pt-5">
        <h3 className="font-heading text-sm text-tbc-navy uppercase tracking-widest">
          Spotlight Rail
        </h3>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="founding_count" className="text-sm">
              Founding businesses always shown
            </Label>
            <Input
              id="founding_count"
              type="number"
              min={0}
              max={50}
              value={settings.spotlight_founding_count}
              onChange={(e) => patch("spotlight_founding_count", Math.max(0, Math.min(50, parseInt(e.target.value) || 0)))}
              className="mt-1.5"
              data-testid="settings-founding-count"
            />
            <p className="text-xs text-gray-400 mt-1 font-body">
              Businesses flagged as Founding Members always fill this many slots first.
            </p>
          </div>

          <div>
            <Label htmlFor="total_count" className="text-sm">
              Total Spotlight slots
            </Label>
            <Input
              id="total_count"
              type="number"
              min={settings.spotlight_founding_count || 1}
              max={100}
              value={settings.spotlight_total_count}
              onChange={(e) => patch("spotlight_total_count", Math.max(1, Math.min(100, parseInt(e.target.value) || 25)))}
              className="mt-1.5"
              data-testid="settings-total-count"
            />
            <p className="text-xs text-gray-400 mt-1 font-body">
              The remaining slots fill with other verified businesses.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between p-3 rounded-lg bg-tbc-cream/30 border border-tbc-gold/20">
          <div>
            <p className="text-sm font-medium text-tbc-navy">Shuffle verified businesses</p>
            <p className="text-xs text-gray-500 font-body">
              Reshuffle non-founding fillers on every visit so the same faces don&apos;t always show first.
            </p>
          </div>
          <Switch
            checked={!!settings.spotlight_shuffle_fillers}
            onCheckedChange={(v) => patch("spotlight_shuffle_fillers", v)}
            data-testid="settings-shuffle-fillers"
          />
        </div>
      </div>

      {/* Trending rail */}
      <div className="space-y-3 border-t border-gray-100 pt-5">
        <h3 className="font-heading text-sm text-tbc-navy uppercase tracking-widest">
          Trending Now Rail
        </h3>

        <div className="flex items-center justify-between p-3 rounded-lg bg-tbc-cream/30 border border-tbc-gold/20">
          <div>
            <p className="text-sm font-medium text-tbc-navy">Show Philanthropy Pick</p>
            <p className="text-xs text-gray-500 font-body">
              Highlights a Philanthropy &amp; Non-Profit business each visit.
            </p>
          </div>
          <Switch
            checked={!!settings.trending_show_philanthropy}
            onCheckedChange={(v) => patch("trending_show_philanthropy", v)}
            data-testid="settings-trending-philanthropy"
          />
        </div>

        <div className="flex items-center justify-between p-3 rounded-lg bg-tbc-cream/30 border border-tbc-gold/20">
          <div>
            <p className="text-sm font-medium text-tbc-navy">Show Influencer Pick</p>
            <p className="text-xs text-gray-500 font-body">
              Highlights an Influencer / Podcaster / Streamer each visit.
            </p>
          </div>
          <Switch
            checked={!!settings.trending_show_influencer}
            onCheckedChange={(v) => patch("trending_show_influencer", v)}
            data-testid="settings-trending-influencer"
          />
        </div>
      </div>

      <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
        <p className="text-xs text-gray-500 font-body">
          {dirty ? "You have unsaved changes." : "All changes saved."}
        </p>
        <Button
          onClick={save}
          disabled={!dirty || saving}
          className="bg-tbc-navy hover:bg-tbc-navy/90 text-white"
          data-testid="settings-save"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Settings
        </Button>
      </div>
    </div>
  );
}
